import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, FileSpreadsheet, FileText, Table as TableIcon } from 'lucide-react';
import { compareCells, exportTableCsv, exportTableXlsx, type ExportColumn, type ExportRow } from '../render/tableExport';
import { isObj, type TableContent } from './shared';
import type { ArtifactViewerProps } from '../types';

// Tabella dati artefatto: ordinamento client-side type-aware (click header,
// asc → desc → nessuno) + export CSV/XLSX (Task #3817).

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

export const TableViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<TableContent>;
  const columns = useMemo<ExportColumn[]>(
    () => (Array.isArray(c?.columns) ? c.columns : []).filter((col): col is ExportColumn => isObj(col) && typeof col.key === 'string'),
    [c?.columns],
  );
  const rows = useMemo<ExportRow[]>(
    () => (Array.isArray(c?.rows) ? c.rows : []).filter(isObj),
    [c?.rows],
  );
  const [sort, setSort] = useState<SortState>(null);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find(cc => cc.key === sort.key);
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => compareCells(a[sort.key], b[sort.key], col?.type) * factor);
  }, [rows, columns, sort]);

  if (columns.length === 0) return <div className="p-4 text-sm text-muted-foreground">Tabella vuota</div>;

  const toggleSort = (key: string): void => {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  const exportBase = artifact.slug || artifact.title || 'tabella';

  return (
    <div className="bg-card rounded-lg overflow-hidden border border-border">
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/60">
        <TableIcon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{rows.length} righe · {columns.length} colonne</span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Esporta CSV"
            onClick={() => exportTableCsv(columns, sortedRows, exportBase)}
          >
            <FileText className="h-3 w-3" /> CSV
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Esporta XLSX"
            onClick={() => { void exportTableXlsx(columns, sortedRows, exportBase); }}
          >
            <FileSpreadsheet className="h-3 w-3" /> XLSX
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-muted text-xs text-muted-foreground uppercase">
            <tr>
              {columns.map(col => {
                const active = sort?.key === col.key;
                const SortIcon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                return (
                  <th
                    key={col.key}
                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className="px-3 py-2 text-left font-medium"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(col.key)}
                      className={`inline-flex items-center gap-1 uppercase hover:text-foreground transition-colors ${active ? 'text-foreground' : ''}`}
                      title={`Ordina per ${col.label}`}
                    >
                      {col.label}
                      <SortIcon className={`h-3 w-3 ${active ? 'text-violet-600' : 'text-muted-foreground/60'}`} />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, i) => (
              <tr key={i} className="border-t border-border hover:bg-muted">
                {columns.map(col => (
                  <td key={col.key} className="px-3 py-2 text-foreground">
                    {String(row[col.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
